import { useCallback, useEffect, useRef, useState } from 'react';

export type RecorderPhase = 'idle' | 'recording' | 'sending';

export interface VoiceRecorder {
  phase: RecorderPhase;
  seconds: number;
  error: string | null;
  supported: boolean;
  start: () => Promise<void>;
  /** Para a gravação e devolve o áudio; `null` se não houver nada gravado. */
  stop: () => Promise<Blob | null>;
  /** Descarta o que foi gravado e libera o microfone. */
  cancel: () => void;
  setSending: (sending: boolean) => void;
  clearError: () => void;
}

/** Acima disso é caso de ligação, não de mensagem de voz. */
const MAX_SECONDS = 120;

/**
 * Gravação de mensagem de voz no painel.
 *
 * O `MediaRecorder` entrega webm/opus no Chrome e Firefox e mp4 no Safari; a
 * conversão para o formato que o WhatsApp toca como voz acontece no servidor,
 * com ffmpeg — tentar padronizar aqui exigiria uma biblioteca de codec no bundle
 * para resolver o que o servidor já resolve.
 *
 * O microfone é liberado em toda saída (envio, cancelamento, desmontagem): faixa
 * aberta deixa o indicador de gravação aceso no navegador, e o atendente fica
 * achando que ainda está sendo ouvido.
 */
export function useVoiceRecorder(): VoiceRecorder {
  const [phase, setPhase] = useState<RecorderPhase>('idle');
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);

  const supported =
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia;

  const release = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
  }, []);

  useEffect(() => release, [release]);

  const start = useCallback(async () => {
    setError(null);

    if (!supported) {
      setError('Este navegador não permite gravar áudio.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        // Fala ao telefone, não música: estes três ajustes tiram o eco da sala e
        // o ruído de fundo da oficina, que é onde o atendente está.
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });

      const recorder = new MediaRecorder(stream, { mimeType: pickMimeType() });
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      streamRef.current = stream;
      recorderRef.current = recorder;
      recorder.start();

      setSeconds(0);
      setPhase('recording');

      timerRef.current = window.setInterval(() => {
        setSeconds((current) => {
          // Corte automático: o limite existe no servidor, e chegar lá com um
          // arquivo recusado depois de dois minutos falando seria pior.
          if (current + 1 >= MAX_SECONDS) recorderRef.current?.stop();
          return current + 1;
        });
      }, 1000);
    } catch (err) {
      release();
      setPhase('idle');
      setError(
        (err as Error).name === 'NotAllowedError'
          ? 'Permissão de microfone negada pelo navegador.'
          : `Não foi possível abrir o microfone: ${(err as Error).message}`,
      );
    }
  }, [release, supported]);

  const stop = useCallback(async (): Promise<Blob | null> => {
    const recorder = recorderRef.current;
    if (!recorder) return null;

    const blob = await new Promise<Blob | null>((resolve) => {
      recorder.onstop = () => {
        const chunks = chunksRef.current;
        resolve(chunks.length ? new Blob(chunks, { type: recorder.mimeType }) : null);
      };

      if (recorder.state === 'inactive') recorder.onstop?.(new Event('stop'));
      else recorder.stop();
    });

    release();
    return blob;
  }, [release]);

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      // Sem handler, o áudio é descartado junto com o recorder.
      recorder.onstop = null;
      recorder.stop();
    }
    chunksRef.current = [];
    release();
    setPhase('idle');
    setSeconds(0);
  }, [release]);

  return {
    phase,
    seconds,
    error,
    supported,
    start,
    stop,
    cancel,
    setSending: (sending) => setPhase(sending ? 'sending' : 'idle'),
    clearError: () => setError(null),
  };
}

/** O primeiro formato que o navegador aceitar; o servidor converte o resto. */
function pickMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) ?? '';
}
